export const ORDER_TYPE_COURTESY = 2;
export const ORDER_STATUS_SOLD = 23;
export const ORDER_STATUS_BLOCKED = 40;

export interface PaymentDetail { tipopago?: string; moneda?: string; cantidad?: number | string; }
export interface OrderSummaryInput {
  idtipopedido?: number | string;
  idestatus?: number | string;
  estatus?: string;
  total?: number | string;
  moneda?: string;
  detallepago?: PaymentDetail[];
}
export interface CountStatistic { key: string; label: string; count: number; }
export interface MoneyStatistic { key: string; label: string; currency: string; amount: number; }
export interface OrderStatistics {
  statusCounts: CountStatistic[];
  courtesyCount: number;
  blockedCount: number;
  salesByCurrency: MoneyStatistic[];
  payments: MoneyStatistic[];
}

function finiteAmount(value: number | string | undefined): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value || '');
  return isFinite(parsed) ? parsed : 0;
}
function normalizedCurrency(value: string | undefined): string {
  const currency = (value || '').trim().toUpperCase();
  return currency || 'SIN MONEDA';
}
function addMoney(target: { [key: string]: MoneyStatistic }, key: string, label: string, currency: string, amount: number): void {
  if (!target[key]) { target[key] = { key: key, label: label, currency: currency, amount: 0 }; }
  target[key].amount += amount;
}
function values<T>(map: { [key: string]: T }): T[] {
  return Object.keys(map).map(function (key) { return map[key]; });
}

export function aggregateOrderStatistics(orders: OrderSummaryInput[]): OrderStatistics {
  const statusCounts: { [key: string]: CountStatistic } = {};
  const salesByCurrency: { [key: string]: MoneyStatistic } = {};
  const payments: { [key: string]: MoneyStatistic } = {};
  let courtesyCount = 0;
  let blockedCount = 0;

  (orders || []).forEach(function (order) {
    const orderType = Number(order.idtipopedido);
    const status = Number(order.idestatus);
    const isCourtesy = orderType === ORDER_TYPE_COURTESY;
    const isBlocked = !isCourtesy && status === ORDER_STATUS_BLOCKED;
    const countsAsRevenue = !isCourtesy && (status === ORDER_STATUS_SOLD || isBlocked);
    if (isCourtesy) {
      courtesyCount++;
    } else if (!isBlocked) {
      const statusKey = String(order.idestatus === undefined ? 'sin-estatus' : order.idestatus);
      if (!statusCounts[statusKey]) {
        statusCounts[statusKey] = { key: statusKey, label: (order.estatus || 'Sin estatus').trim() || 'Sin estatus', count: 0 };
      }
      statusCounts[statusKey].count++;
    }
    if (isBlocked) { blockedCount++; }
    if (!countsAsRevenue) { return; }
    const orderCurrency = normalizedCurrency(order.moneda);
    addMoney(salesByCurrency, orderCurrency, 'Venta del día', orderCurrency, finiteAmount(order.total));
    const details = Array.isArray(order.detallepago) ? order.detallepago : [];
    details.forEach(function (payment) {
      const method = (payment.tipopago || 'Sin método').trim() || 'Sin método';
      const currency = normalizedCurrency(payment.moneda || order.moneda);
      addMoney(payments, method.toLowerCase() + '|' + currency, method, currency, finiteAmount(payment.cantidad));
    });
  });
  return {
    statusCounts: values(statusCounts),
    courtesyCount: courtesyCount,
    blockedCount: blockedCount,
    salesByCurrency: values(salesByCurrency),
    payments: values(payments)
  };
}

export function formatMoney(amount: number, currency: string): string {
  if (currency && currency !== 'SIN MONEDA') {
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency, minimumFractionDigits: 2 }).format(amount);
    } catch (error) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }
  return amount.toFixed(2) + ' (sin moneda)';
}



// WEBPACK FOOTER //
// ./src/utils/order-statistics.ts